import { styled } from '@mui/material/styles';
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';

// Styled DataGrid Pro with StyledTable appearance
export const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  border: 'none',
  borderRadius: '10px',
  boxShadow: '0 10px 30px rgba(146,153,184,0.15)',

  // Toolbar styling
  '& .MuiDataGrid-toolbarContainer': {
    backgroundColor: '#FFFFFF',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '12px 16px',
    minHeight: '56px',
  },

  // Header styling to match StyledTableHead
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F4F5F7 !important',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  '& .MuiDataGrid-columnHeader': {
    padding: '16px',
    backgroundColor: '#F4F5F7 !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },

  '& .MuiDataGrid-columnHeaderTitleContainer': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100%',
    width: '100%',
  },

  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: '12px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    lineHeight: 1,
  },

  '& .MuiDataGrid-sortIcon': {
    color: theme.palette.text.secondary,
    opacity: 0.7,
    backgroundColor: 'transparent !important',
    marginLeft: '4px',
  },

  '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
    color: theme.palette.primary.main,
    opacity: 1,
    backgroundColor: 'transparent !important',
  },

  '& .MuiDataGrid-iconButtonContainer': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiDataGrid-iconButton': {
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },

  '& .MuiIconButton-root': {
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}10 !important`,
    },
  },

  '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
    backgroundColor: 'transparent !important',
  },

  // Row styling to match StyledTableBody
  '& .MuiDataGrid-row': {
    minHeight: '52px !important',
    maxHeight: '52px !important',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.primary.main}08`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}15`,
      },
    },
  },

  '& .MuiDataGrid-cell': {
    fontSize: '13px',
    color: theme.palette.text.primary,
    padding: '14px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '52px !important',
    maxHeight: '52px !important',
    display: 'flex',
    alignItems: 'center',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },

  // Footer/Pagination styling to match StyledTablePagination
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: '52px',
  },

  '& .MuiTablePagination-root': {
    overflow: 'hidden',
  },

  '& .MuiTablePagination-toolbar': {
    padding: '16px',
  },

  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: '13px',
    color: theme.palette.text.secondary,
    margin: 0,
  },

  '& .MuiTablePagination-select': {
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    padding: '6px 32px 6px 12px',
    fontSize: '13px',
  },

  '& .MuiTablePagination-actions .MuiIconButton-root': {
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    margin: '0 4px',
    padding: '6px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}10`,
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
    },
    '&.Mui-disabled': {
      borderColor: theme.palette.divider,
      opacity: 0.5,
    },
  },

  // Remove default selection checkboxes (optional - remove if you need them)
  '& .MuiDataGrid-checkboxInput': {
    color: theme.palette.primary.main,
  },

  // Column menu styling
  '& .MuiDataGrid-menuIcon': {
    visibility: 'hidden',
    width: 0,
  },

  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
    visibility: 'visible',
    width: 'auto',
  },

  '& .MuiDataGrid-menuIconButton': {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}10`,
    },
  },

  // Loading overlay
  '& .MuiDataGrid-overlay': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },

  // Scrollbar styling
  '& .MuiDataGrid-virtualScroller': {
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#F4F5F7',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.divider,
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: theme.palette.text.secondary,
      },
    },
  },
}));

// Wrapper component with default props
interface StyledDataGridWrapperProps extends Omit<DataGridProProps, 'ref'> {
  height?: number | string;
}

export const StyledDataGridWrapper = ({ height = 600, ...props }: StyledDataGridWrapperProps) => {
  return (
    <Box sx={{ height, width: '100%' }}>
      <StyledDataGrid
        {...props}
        columnHeaderHeight={props.columnHeaderHeight || 52}
        pagination
        pageSizeOptions={props.pageSizeOptions || [5, 10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          ...props.initialState,
        }}
        disableRowSelectionOnClick
      />
    </Box>
  );
};

export default StyledDataGridWrapper;
